import { FormattedMessage } from "react-intl";
import { Link } from "react-router";
import { TileArtwork } from "@/components/grow/TileArtwork";

export type CatalogueStatus = "in-progress" | "not-started" | "completed";

// One tile of Grow · Play: a questionnaire group, a group of games, a single
// game or a per-Power self-assessment. Shared by the Play catalogue and the
// game group page, so a list reads the same on both.
export interface CatalogueEntry {
  id: string;
  title: string;
  description: string;
  href: string;
  kind: "group" | "game" | "power";
  status: CatalogueStatus;
  progress: string;
  imageSrc?: string;
  imageProgress?: { answered: number; total: number };
  // The generated tile when there is no picture. Without either the tile is
  // text only: the Top 10 lists have no picture, and a pattern read as one.
  hue?: number;
}

const BADGE = "rounded bg-ink px-2 py-1 text-smycles-micro uppercase tracking-widest text-ink";

export function CatalogueCard({ entry }: { entry: CatalogueEntry }) {
  // One anchor per catalogue entry, keyed by the entry's own id, so a spec can
  // name the tile it means: the catalogue mixes questionnaire groups (uuid) and
  // powers (element key), and both are addressed by id in the E2E suite.
  const anchorId = `grow-game-${entry.id}`;
  const artwork = entry.imageSrc !== undefined || entry.hue !== undefined;
  const kind =
    entry.kind === "power" ? (
      <FormattedMessage id="grow.assess.selfAssessmentKind" defaultMessage="Self-assessment" />
    ) : entry.kind === "game" ? (
      <FormattedMessage id="grow.play.game.kind" defaultMessage="Game" />
    ) : (
      <FormattedMessage id="grow.play.group.kind" defaultMessage="Questionnaire" />
    );
  return (
    <Link
      id={anchorId}
      to={entry.href}
      className="box flex flex-col overflow-hidden bg-surface transition hover:ring-accent-decoration/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink-accent-ui"
    >
      {artwork ? (
        <div className="relative">
          <TileArtwork
            imageSrc={entry.imageSrc}
            hue={entry.hue ?? 0}
            variant="grid"
            className="aspect-[16/10]"
            imageProgress={entry.imageProgress}
          />
          <span className={`absolute left-2 top-2 ${BADGE}`}>{kind}</span>
        </div>
      ) : null}
      <div className="flex flex-1 flex-col px-4 py-3">
        {artwork ? null : <span className={`mb-3 self-start ${BADGE}`}>{kind}</span>}
        <h3 id={`${anchorId}-name`} className="text-smycles-body font-medium">
          {entry.title}
        </h3>
        {entry.description ? (
          <p
            id={`${anchorId}-caption`}
            className="mt-1 text-smycles-label text-ink-muted"
          >
            {entry.description}
          </p>
        ) : null}
        <p id={`${anchorId}-status`} className="mt-auto pt-4 text-smycles-meta text-ink-muted">
          {entry.status === "completed" ? (
            <FormattedMessage
              id="grow.assess.completed"
              defaultMessage="Completed"
            />
          ) : entry.status === "in-progress" ? (
            <FormattedMessage
              id="grow.assess.inProgress"
              defaultMessage="In progress"
            />
          ) : (
            <FormattedMessage
              id="grow.assess.notStarted"
              defaultMessage="Not started"
            />
          )}
          {entry.progress ? ` · ${entry.progress}` : ""}
        </p>
      </div>
    </Link>
  );
}
